import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/lib/api";
import { QUERY_KEYS } from "@/lib/constants";
import type {
  Refund,
  CreateRefundRequest,
  RefundStatus,
  PaginatedResponse,
} from "@/types";

interface RefundFilters {
  status?: RefundStatus;
  orderId?: string;
  page?: number;
  limit?: number;
}

export function useRefunds(filters?: RefundFilters) {
  return useQuery({
    queryKey: [...QUERY_KEYS.refunds, filters],
    queryFn: () =>
      apiClient.get<PaginatedResponse<Refund>>("/refunds", {
        status: filters?.status,
        orderId: filters?.orderId,
        page: filters?.page,
        limit: filters?.limit,
      }),
    placeholderData: (prev) => prev,
  });
}

export function useRefund(id: string) {
  return useQuery({
    queryKey: QUERY_KEYS.refund(id),
    queryFn: () => apiClient.get<Refund>(`/refunds/${id}`),
    enabled: !!id,
  });
}

export function useCreateRefund() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateRefundRequest) =>
      apiClient.post<Refund>("/refunds", data, {
        idempotencyKey: crypto.randomUUID(),
      }),
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.refunds });
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.orderRefunds(vars.orderId),
      });
      queryClient.invalidateQueries({ queryKey: ["orders"] });
    },
  });
}

export function useUpdateRefundStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      status,
    }: {
      id: string;
      status: "approved" | "rejected";
    }) => apiClient.patch<Refund>(`/refunds/${id}/status`, { status }),
    onSuccess: (updated, { id }) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.refunds });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.refund(id) });
      queryClient.invalidateQueries({ queryKey: ["orders"] });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.shifts });
      queryClient.invalidateQueries({ queryKey: ["reports"] });

      if (updated?.order?.id) {
        queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.order(updated.order.id),
        });
      }
    },
  });
}
